<template>
  <div
    class="rate-container"
    role="radiogroup"
    :aria-label="ariaLabel"
    :aria-describedby="describedBy"
  >
    <button
      v-for="star in maxStars"
      :key="star"
      type="button"
      class="star-button"
      :class="getStarClasses(star)"
      :aria-label="`Rate ${star} star${star !== 1 ? 's' : ''} out of ${maxStars}`"
      :aria-checked="modelValue === star"
      role="radio"
      @click="handleStarClick(star)"
      @mouseenter="handleMouseEnter(star)"
      @mouseleave="handleMouseLeave"
      @focus="handleFocus(star)"
      @blur="handleBlur"
      @keydown="handleKeyDown"
    >
      <Icon
        name="lucide:star"
        :size="size"
        mode="svg"
      />
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
const props = defineProps({
  modelValue: { type: Number, required: false, default: 0 },
  maxStars: { type: Number, required: false, default: 5 },
  size: { type: [String, Number], required: false, default: "24" },
  ariaLabel: { type: String, required: false, default: "Rating" },
  describedBy: { type: String, required: false, default: void 0 }
});
const emit = defineEmits(["update:modelValue", "change"]);
const hoveredStar = ref(0);
const focusedStar = ref(0);
const isFocused = ref(false);
const displayValue = computed(() => {
  if (isFocused.value && focusedStar.value > 0) {
    return focusedStar.value;
  }
  if (hoveredStar.value > 0) {
    return hoveredStar.value;
  }
  return props.modelValue;
});
function getStarClasses(star) {
  return {
    "star-filled": star <= displayValue.value,
    "star-empty": star > displayValue.value,
    "star-hovered": hoveredStar.value > 0 && star <= hoveredStar.value,
    "star-focused": isFocused.value && star === focusedStar.value,
    "star-selected": star === props.modelValue
  };
}
function handleStarClick(star) {
  const newValue = star === props.modelValue ? 0 : star;
  emit("update:modelValue", newValue);
  emit("change", newValue);
}
function handleMouseEnter(star) {
  hoveredStar.value = star;
}
function handleMouseLeave() {
  hoveredStar.value = 0;
}
function handleFocus(star) {
  isFocused.value = true;
  focusedStar.value = star;
}
function handleBlur() {
  isFocused.value = false;
  focusedStar.value = 0;
}
function handleKeyDown(event) {
  let newValue = focusedStar.value || props.modelValue || 1;
  switch (event.key) {
    case "ArrowRight":
    case "ArrowUp":
      event.preventDefault();
      newValue = Math.min(newValue + 1, props.maxStars);
      focusedStar.value = newValue;
      break;
    case "ArrowLeft":
    case "ArrowDown":
      event.preventDefault();
      newValue = Math.max(newValue - 1, 1);
      focusedStar.value = newValue;
      break;
    case "Home":
      event.preventDefault();
      newValue = 1;
      focusedStar.value = newValue;
      break;
    case "End":
      event.preventDefault();
      newValue = props.maxStars;
      focusedStar.value = newValue;
      break;
    case "Enter":
    case " ":
      event.preventDefault();
      emit("update:modelValue", newValue);
      emit("change", newValue);
      break;
    case "Escape":
      event.preventDefault();
      focusedStar.value = props.modelValue || 0;
      break;
  }
}
</script>

<style scoped>
.rate-container{--color-star-empty:#9cb4e4;--color-star-filled:#1850c1;--color-star-hover:#1850c1;--color-focus-ring:#1850c1;align-items:center;display:inline-flex;gap:8px}.star-button{align-items:center;background:none;border:none;border-radius:4px;cursor:pointer;display:flex;justify-content:center;padding:0;position:relative;transition:all .2s ease}.star-button:hover{transform:scale(1.1)}.star-button:focus-visible{outline:2px solid var(--color-focus-ring);outline-offset:2px}svg{transition:all .2s ease;fill:var(--color-star-empty);color:var(--color-star-empty)}.star-button.star-filled svg{fill:var(--color-star-filled);color:var(--color-star-filled)}.star-button.star-filled svg :deep(path){fill:var(--color-star-filled)}.star-button.star-hovered svg{fill:var(--color-star-hover);color:var(--color-star-hover)}.star-button.star-focused svg{filter:brightness(1.2)}.star-button.star-selected svg{fill:var(--color-star-filled);color:var(--color-star-filled)}
</style>
